// สร้าง/แก้ไขข้อสอบ — ชื่อ ห้องที่ใช้ รายการข้อ เฉลย และคะแนนรายข้อ
//
// มีคำตอบของนักเรียนแล้ว = ล็อกโครง (จำนวนข้อ ชนิดข้อ จำนวนตัวเลือก) เพราะกระดาษที่พิมพ์ไป
// จัดตำแหน่งตามโครงนั้น แต่แก้เฉลยกับคะแนนได้ตลอด (server ตรวจซ้ำเสมอ ดู examModel.updateExam)
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, TYPE_LABELS, fmt, isObjective, keyLabel, readYearId, roomLabel, showError } from './examUtils'
import { MAX_BUBBLE_QUESTIONS } from './sheetLayout'

const groupKey = (t) => `${t.sub_subid}|${t.sub_semester}`

// โครงที่กระดาษคำตอบจัดตำแหน่งตาม — เปลี่ยนแล้วกระดาษที่พิมพ์ไปแล้วใช้ไม่ได้
const shapeOf = (qs, choices) => `${choices}:${qs.map((q) => q.eq_type).join(',')}`

export default function ExamEditor() {
  const { examId } = useParams()
  const isNew = !examId
  const navigate = useNavigate()
  const { setLoading } = useLoading()
  const [, setSkin] = useState('')

  const [name, setName] = useState('')
  const [choices, setChoices] = useState(4)
  const [questions, setQuestions] = useState([])
  const [teachIds, setTeachIds] = useState(new Set())
  const [originalTeachIds, setOriginalTeachIds] = useState(new Set())
  const [teachings, setTeachings] = useState([])
  const [yearId, setYearId] = useState(isNew ? readYearId() : null)
  const [responseCount, setResponseCount] = useState(0)
  const [roomResponses, setRoomResponses] = useState({})
  const [originalShape, setOriginalShape] = useState(null)

  const [addCount, setAddCount] = useState(10)
  const [addType, setAddType] = useState('choice')
  const [addPoints, setAddPoints] = useState(1)
  const [keyString, setKeyString] = useState('')

  const locked = responseCount > 0
  const shapeChanged = originalShape !== null && shapeOf(questions, choices) !== originalShape

  useEffect(() => {
    if (isNew) return
    setLoading(true)
    axiosInstance.get(`${API}/exams/${examId}`)
      .then((res) => {
        const { exam, questions: qs, rooms, response_count: count } = res.data
        setName(exam.exam_name)
        setChoices(Number(exam.exam_choices))
        setQuestions(qs.map((q) => ({ eq_type: q.eq_type, eq_key: keyLabel(q.eq_key), eq_points: q.eq_points })))
        setOriginalShape(shapeOf(qs, Number(exam.exam_choices)))
        const ids = new Set(rooms.map((r) => r.teach_id))
        setTeachIds(ids)
        setOriginalTeachIds(ids)
        setRoomResponses(Object.fromEntries(rooms.map((r) => [r.teach_id, r.response_count])))
        setResponseCount(count)
        setYearId(String(exam.exam_year_id))
      })
      .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [examId, isNew, setLoading])

  // ครูที่ยังไม่เคยเปิดหน้าอื่นของครูผู้สอนจะไม่มีปีการศึกษาค้างใน localStorage
  // ใช้ปีล่าสุดแทน ไม่งั้นรายการห้องว่างโดยไม่บอกว่าทำไม
  useEffect(() => {
    if (!isNew || yearId) return
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => { if (res.data.length) setYearId(String(res.data[0].year_id)) })
      .catch(() => {})
  }, [isNew, yearId])

  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`${API}/exams/teachings`, { params: { year_id: yearId } })
      .then((res) => setTeachings(res.data))
      .catch((err) => showError(err, 'โหลดรายวิชาที่สอนไม่สำเร็จ'))
  }, [yearId])

  // วิชาเดียวกัน (รหัส + ภาค) หลายห้อง = ข้อสอบชุดเดียวกัน
  const groups = useMemo(() => {
    const map = new Map()
    for (const t of teachings) {
      if (!map.has(groupKey(t))) map.set(groupKey(t), { key: groupKey(t), first: t, rooms: [] })
      map.get(groupKey(t)).rooms.push(t)
    }
    return [...map.values()]
  }, [teachings])

  const selectedGroup = useMemo(() => {
    const first = teachings.find((t) => teachIds.has(t.teach_id))
    return first ? groupKey(first) : null
  }, [teachings, teachIds])

  const toggleRoom = (id) => {
    setTeachIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const objectiveCount = questions.filter(isObjective).length
  const total = questions.reduce((s, q) => s + (Number(q.eq_points) || 0), 0)

  const addQuestions = () => {
    const n = Math.max(1, Math.min(150, Number(addCount) || 0))
    const points = Number(addPoints) || 1
    setQuestions((prev) => [...prev, ...Array.from({ length: n }, () => ({ eq_type: addType, eq_key: '', eq_points: points }))])
  }

  const updateQuestion = (index, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)))
  }

  const removeQuestion = (index) => setQuestions((prev) => prev.filter((_, i) => i !== index))

  // วางเฉลยต่อกันทีเดียว เช่น "13422ถผ" — ตัวอักษรละหนึ่งข้อ ไล่ใส่ข้อปรนัยตามลำดับ ข้ามข้ออัตนัย
  const applyKeyString = () => {
    const chars = [...keyString.replace(/\s+/g, '')]
    if (!chars.length) return
    let k = 0
    setQuestions((prev) => prev.map((q) => {
      if (!isObjective(q) || k >= chars.length) return q
      return { ...q, eq_key: chars[k++] }
    }))
    setKeyString('')
  }

  const save = async () => {
    if (!name.trim()) return Swal.fire({ icon: 'warning', title: 'กรุณาระบุชื่อข้อสอบ' })
    if (!teachIds.size) return Swal.fire({ icon: 'warning', title: 'กรุณาเลือกห้องที่ใช้ข้อสอบนี้' })
    if (!questions.length) return Swal.fire({ icon: 'warning', title: 'ยังไม่มีข้อสอบสักข้อ' })
    const payload = {
      exam_name: name,
      exam_choices: choices,
      questions: questions.map((q) => ({ eq_type: q.eq_type, eq_key: q.eq_key, eq_points: q.eq_points })),
    }
    setLoading(true)
    try {
      if (isNew) {
        const res = await axiosInstance.post(`${API}/exams`, { ...payload, teach_ids: [...teachIds] })
        navigate(`/teaching/exams/${res.data.exam_id}`, { replace: true })
        return
      }
      await axiosInstance.put(`${API}/exams/${examId}`, payload)
      const roomsChanged = teachIds.size !== originalTeachIds.size || [...teachIds].some((id) => !originalTeachIds.has(id))
      if (roomsChanged) await axiosInstance.put(`${API}/exams/${examId}/rooms`, { teach_ids: [...teachIds] })
      navigate(`/teaching/exams/${examId}`)
    } catch (err) {
      showError(err, 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  const remove = async () => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'ลบข้อสอบชุดนี้?',
      text: responseCount
        ? `คำตอบของนักเรียน ${responseCount} คนจะหายไปด้วย (คะแนนที่ส่งเข้าตารางคะแนนแล้วยังอยู่)`
        : 'ลบแล้วกู้คืนไม่ได้',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    })
    if (!ok.isConfirmed) return
    setLoading(true)
    try {
      await axiosInstance.delete(`${API}/exams/${examId}`)
      navigate('/teaching/exams', { replace: true })
    } catch (err) {
      showError(err, 'ลบไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div className="me-auto">
            <Link to={isNew ? '/teaching/exams' : `/teaching/exams/${examId}`} className="fs-sm">
              <i className="ri-arrow-left-line"></i> กลับ
            </Link>
            <h4 className="main-title mb-0">{isNew ? 'สร้างข้อสอบ' : 'แก้ไขข้อสอบ'}</h4>
          </div>
          {!isNew && (
            <Button variant="outline-danger" size="sm" onClick={remove}>
              <i className="ri-delete-bin-line"></i> ลบข้อสอบ
            </Button>
          )}
          <Button size="sm" onClick={save}>
            <i className="ri-save-line"></i> บันทึก
          </Button>
        </div>

        {locked && (
          <Alert variant="info" className="fs-sm">
            มีคำตอบของนักเรียนแล้ว {responseCount} คน — แก้เฉลยและคะแนนรายข้อได้ (ผลสอบคิดใหม่ให้ทันที)
            แต่เพิ่ม/ลบข้อ เปลี่ยนชนิดข้อ หรือจำนวนตัวเลือกไม่ได้ เพราะกระดาษที่พิมพ์ไปจัดตำแหน่งตามโครงเดิม
          </Alert>
        )}

        {!locked && shapeChanged && (
          <Alert variant="warning" className="fs-sm">
            จำนวนข้อ ชนิดข้อ หรือจำนวนตัวเลือกเปลี่ยนจากเดิม — ถ้าพิมพ์กระดาษคำตอบไปแล้ว ต้องพิมพ์ใหม่ทั้งหมด
            เพราะตำแหน่งข้อบนกระดาษเดิมจะไม่ตรงกับข้อสอบชุดนี้
          </Alert>
        )}

        <Row className="g-3">
          <Col lg={4}>
            <Card className="card-one mb-3">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อข้อสอบ</Form.Label>
                  <Form.Control value={name} maxLength={200} placeholder="เช่น สอบกลางภาค" onChange={(e) => setName(e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>ตัวเลือกของข้อปรนัย</Form.Label>
                  <Form.Select value={choices} disabled={locked} onChange={(e) => setChoices(Number(e.target.value))}>
                    <option value={4}>4 ตัวเลือก (ก–ง)</option>
                    <option value={5}>5 ตัวเลือก (ก–จ)</option>
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">ห้องที่ใช้ข้อสอบนี้</Card.Title></Card.Header>
              <Card.Body>
                {groups.length === 0 && <div className="text-secondary fs-sm">ไม่พบวิชาที่สอนในปีการศึกษานี้</div>}
                {groups.map((g) => {
                  const disabled = selectedGroup && selectedGroup !== g.key
                  return (
                    <div key={g.key} className={`mb-3 ${disabled ? 'opacity-50' : ''}`}>
                      <div className="fw-semibold fs-sm">
                        {g.first.sub_subid} {g.first.sub_subname}
                        <span className="text-secondary fw-normal"> · ภาค {g.first.sub_semester}</span>
                      </div>
                      <div className="d-flex flex-wrap gap-3 mt-1">
                        {g.rooms.map((r) => {
                          const used = roomResponses[r.teach_id] > 0
                          return (
                            <Form.Check
                              key={r.teach_id}
                              id={`room-${r.teach_id}`}
                              label={`${roomLabel(r)}${used ? ` (${roomResponses[r.teach_id]} คน)` : ''}`}
                              checked={teachIds.has(r.teach_id)}
                              disabled={disabled || (used && teachIds.has(r.teach_id))}
                              onChange={() => toggleRoom(r.teach_id)}
                            />
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
                <div className="fs-xs text-secondary">
                  ข้อสอบชุดหนึ่งใช้ได้หลายห้องของวิชาเดียวกัน ห้องที่มีคำตอบแล้วเอาออกไม่ได้
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="card-one">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0 me-auto">
                  ข้อสอบ {questions.length} ข้อ · เต็ม {fmt(total)} คะแนน
                </Card.Title>
                {objectiveCount > MAX_BUBBLE_QUESTIONS && (
                  <Badge bg="danger">ข้อปรนัยเกิน {MAX_BUBBLE_QUESTIONS} ข้อ ไม่พอหนึ่งแผ่น</Badge>
                )}
              </Card.Header>
              <Card.Body>
                {!locked && (
                  <InputGroup size="sm" className="mb-3 flex-nowrap">
                    <InputGroup.Text>เพิ่ม</InputGroup.Text>
                    <Form.Control type="number" min={1} max={150} value={addCount} style={{ maxWidth: 70 }}
                      onChange={(e) => setAddCount(e.target.value)} />
                    <InputGroup.Text>ข้อ</InputGroup.Text>
                    <Form.Select value={addType} style={{ maxWidth: 130 }} onChange={(e) => setAddType(e.target.value)}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </Form.Select>
                    <InputGroup.Text>ข้อละ</InputGroup.Text>
                    <Form.Control type="number" min={0.5} step={0.5} value={addPoints} style={{ maxWidth: 70 }}
                      onChange={(e) => setAddPoints(e.target.value)} />
                    <InputGroup.Text>คะแนน</InputGroup.Text>
                    <Button onClick={addQuestions}><i className="ri-add-line"></i></Button>
                  </InputGroup>
                )}

                {objectiveCount > 0 && (
                  <InputGroup size="sm" className="mb-3">
                    <InputGroup.Text>วางเฉลยต่อกัน</InputGroup.Text>
                    <Form.Control
                      value={keyString}
                      placeholder="เช่น 13422 หรือ กคงขข · ใช่/ไม่ใช่ใช้ ถ ผ — ไล่ใส่ข้อปรนัยตามลำดับ"
                      onChange={(e) => setKeyString(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && applyKeyString()}
                    />
                    <Button variant="outline-primary" onClick={applyKeyString}>ใส่เฉลย</Button>
                  </InputGroup>
                )}

                <div className="table-responsive" style={{ maxHeight: '65vh' }}>
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="sticky-top bg-body">
                      <tr>
                        <th style={{ width: 50 }}>ข้อ</th>
                        <th style={{ width: 130 }}>ชนิด</th>
                        <th>เฉลย</th>
                        <th style={{ width: 90 }}>คะแนน</th>
                        {!locked && <th style={{ width: 40 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((q, i) => (
                        <tr key={i}>
                          <td className="fw-semibold">{i + 1}</td>
                          <td>
                            <Form.Select size="sm" value={q.eq_type} disabled={locked}
                              onChange={(e) => updateQuestion(i, { eq_type: e.target.value, eq_key: '' })}>
                              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            {q.eq_type === 'written' ? (
                              <span className="text-secondary fs-sm">ครูกรอกคะแนนเอง</span>
                            ) : (
                              <Form.Control
                                size="sm"
                                value={q.eq_key ?? ''}
                                style={{ maxWidth: 160 }}
                                placeholder={q.eq_type === 'truefalse' ? 'ถ / ผ' : choices === 5 ? 'ก–จ หรือ 1–5' : 'ก–ง หรือ 1–4'}
                                isInvalid={!q.eq_key}
                                onChange={(e) => updateQuestion(i, { eq_key: e.target.value })}
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" min={0.5} step={0.5} value={q.eq_points}
                              onChange={(e) => updateQuestion(i, { eq_points: e.target.value })} />
                          </td>
                          {!locked && (
                            <td>
                              <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeQuestion(i)}>
                                <i className="ri-close-line"></i>
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="fs-xs text-secondary mt-2">
                  เฉลยหลายคำตอบคั่นด้วยจุลภาค เช่น <code>ก, ค</code> (ตอบข้อใดก็ถูก) · ใส่ <code>*</code> เพื่อยกเลิกข้อ ทุกคนได้คะแนน ·
                  ข้อที่ยังไม่มีเฉลยจะยังไม่ถูกคิดคะแนนให้ใคร
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  )
}
